import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/constants";
import { MapPin, FileText, Workflow, Banknote, TrendingUp, Clock, CheckCircle2, Hammer } from "lucide-react";
import { Card } from "@/components/ui/card";
import { PendingPetitionsWidget } from "@/components/PendingPetitionsWidget";
import { EditableTitle } from "@/components/EditableTitle";
import { toast } from "sonner";
import { PeriodPresets, DASHBOARD_PRESETS, matchPreset } from "@/components/PeriodPresets";

const EMPTY_RANGE = { date_from: "", date_to: "" };

const DEFAULT_TITLES = {
  villages: "Toplam Köy",
  pending_petitions_kpi: "Bekleyen Dilekçe",
  active_infra: "Aktif Altyapı",
  budget: "Yıllık Bütçe",
  work_buckets: "Çalışma Kayıt Dağılımı",
  infra_status: "Altyapı Projeleri Durumu",
  pending_petitions: "Sonuçlanmayı Bekleyen Dilekçeler",
};

const Stat = ({ icon: Icon, wkey, titles, onSave, value, hint, color = "emerald", testId }) => (
  <Card className="p-6 border border-slate-200 shadow-sm" data-testid={testId}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <EditableTitle widgetKey={wkey} title={titles[wkey]} onSave={onSave} as="div"
                       className="text-xs uppercase tracking-wider text-slate-500 font-semibold truncate" />
        <div className="font-display text-3xl font-bold text-slate-900 mt-2">{value}</div>
        {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
      </div>
      <div className={`h-10 w-10 shrink-0 rounded-md bg-${color}-50 text-${color}-600 flex items-center justify-center`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </Card>
);

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [titles, setTitles] = useState(DEFAULT_TITLES);
  const [range, setRange] = useState(EMPTY_RANGE);
  const hasRange = !!(range.date_from || range.date_to);

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(range).filter(([, v]) => v));
    api.get("/stats/dashboard", { params }).then((r) => setStats(r.data))
      .catch(() => toast.error("Panel verileri yüklenemedi"));
  }, [range]);

  useEffect(() => {
    api.get("/dashboard/titles").then((r) => setTitles({ ...DEFAULT_TITLES, ...r.data }));
  }, []);

  const saveTitle = async (key, title) => {
    try {
      await api.put(`/dashboard/titles/${key}`, { title });
      setTitles((t) => ({ ...t, [key]: title }));
      toast.success("Başlık güncellendi");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || "Başlık kaydedilemedi");
      return false;
    }
  };
  const tp = { titles, onSave: saveTitle };

  if (!stats) {
    return (
      <AppLayout title="Panel" subtitle="Genel bakış ve özet istatistikler">
        <div className="text-slate-500">Yükleniyor...</div>
      </AppLayout>
    );
  }

  const infraStatus = stats.infra_status || {};

  return (
    <AppLayout
      title="Panel"
      subtitle={`Köylere Hizmet Götürme Birliği — ${hasRange ? `Dönem: ${stats.period}` : `${stats.current_year} Yılı Özeti`}`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="dashboard-period-bar">
        <button type="button" onClick={() => setRange(EMPTY_RANGE)} data-testid="dashboard-preset-all"
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${!hasRange
                  ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-300 hover:border-emerald-600"}`}>
          Tüm zamanlar
        </button>
        <PeriodPresets presets={DASHBOARD_PRESETS} active={hasRange ? matchPreset(DASHBOARD_PRESETS, range) : undefined}
                       onPick={(p) => setRange(p.range())} testPrefix="dashboard" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Stat icon={MapPin} wkey="villages" {...tp} value={stats.villages_count}
              hint="Sistemde tanımlı köy" color="emerald"
              testId="kpi-total-villages-card" />
        <Stat icon={FileText} wkey="pending_petitions_kpi" {...tp} value={stats.petitions_pending}
              hint={`${stats.petitions_in_progress} işlemde`} color="orange"
              testId="kpi-pending-petitions-card" />
        <Stat icon={Workflow} wkey="active_infra" {...tp} value={stats.active_infra}
              hint="Planlama + Yapım aşaması" color="blue"
              testId="kpi-active-infra-card" />
        <Stat icon={Banknote} wkey="budget" {...tp} value={formatMoney(stats.budget_total)}
              hint={`Gerçekleşen: ${formatMoney(stats.actual_total)}${hasRange ? " · dönemdeki yıllar" : ` · ${stats.current_year}`}`} color="purple"
              testId="kpi-budget-card" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card className="p-6 border border-slate-200 shadow-sm" data-testid="work-buckets-card">
          <EditableTitle widgetKey="work_buckets" {...tp} title={titles.work_buckets}
                         className="font-display text-lg font-semibold text-slate-900" />
          <p className="text-xs text-slate-500 mt-1">{hasRange ? "Seçilen dönemdeki kayıtların zaman boyutuna göre dağılımı" : "Zaman boyutuna göre toplam kayıt sayısı"}</p>
          <div className="mt-6 space-y-4">
            {[
              { k: "previous", label: hasRange ? "Önceki Yıl" : `Önceki Yıl (${stats.current_year - 1})`, icon: Clock, color: "amber" },
              { k: "current", label: hasRange ? "Cari Yıl" : `Cari Yıl (${stats.current_year})`, icon: Hammer, color: "emerald" },
              { k: "next", label: hasRange ? "Sonraki Yıl" : `Sonraki Yıl (${stats.current_year + 1})`, icon: TrendingUp, color: "blue" },
            ].map((b) => {
              const count = stats.work_log_buckets?.[b.k] || 0;
              const max = Math.max(1, ...Object.values(stats.work_log_buckets || {}));
              const pct = (count / max) * 100;
              return (
                <div key={b.k} data-testid={`work-bucket-${b.k}`}>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <div className="flex items-center gap-2 text-slate-700">
                      <b.icon className={`h-4 w-4 text-${b.color}-600`} />
                      {b.label}
                    </div>
                    <div className="font-mono text-slate-900 font-semibold">{count}</div>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full bg-${b.color}-500 transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-6 border border-slate-200 shadow-sm" data-testid="infra-status-card">
          <EditableTitle widgetKey="infra_status" {...tp} title={titles.infra_status}
                         className="font-display text-lg font-semibold text-slate-900" />
          <p className="text-xs text-slate-500 mt-1">{hasRange ? "Seçilen dönemle çakışan projelerin durum dağılımı" : "Toplam projelerin durum dağılımı"}</p>
          <div className="mt-6 grid grid-cols-2 gap-4">
            {[
              { k: "planlama", label: "Planlama", color: "amber" },
              { k: "yapim_asamasi", label: "Yapım Aşaması", color: "blue" },
              { k: "tamamlandi", label: "Tamamlandı", color: "emerald" },
              { k: "kesin_kabul", label: "Kesin Kabul", color: "purple" },
            ].map((s) => (
              <div key={s.k} className={`p-4 rounded-md border bg-${s.color}-50 border-${s.color}-200`}
                   data-testid={`infra-status-${s.k}`}>
                <div className="text-xs text-slate-700 font-medium">{s.label}</div>
                <div className={`font-display text-2xl font-bold text-${s.color}-700 mt-1`}>
                  {infraStatus[s.k] || 0}
                </div>
              </div>
            ))}
          </div>
          {stats.budget_total > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-700">Bütçe Gerçekleşme</span>
                <span className="font-mono font-semibold text-slate-900">
                  {Math.round((stats.actual_total / stats.budget_total) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-2">
                <div className="h-full bg-emerald-600"
                     style={{ width: `${Math.min(100, (stats.actual_total / stats.budget_total) * 100)}%` }} />
              </div>
            </div>
          )}
        </Card>
      </div>
      <PendingPetitionsWidget range={range} title={
        <EditableTitle widgetKey="pending_petitions" {...tp} title={titles.pending_petitions}
                       className="font-display text-lg font-semibold text-slate-900" />
      } />
    </AppLayout>
  );
}
